export const AUTO_APPLY_DISCOUNT_SHOPLIFT_ID =
  '01a01b48-b443-72b2-b46e-498cd184b751';

export const APPLIED_DISCOUNT_STORAGE_KEY = 'applied_discount_code';

const AUTO_APPLY_CONFIG_KEY = 'autoApplyDiscountCodeTest';
const AUTO_APPLIED_DISCOUNT_STORAGE_KEY =
  'auto_applied_discount_code_test_value';
const AUTO_APPLY_ACTIVE_EVENT = 'autoApplyDiscountCodeTestActive';
const SHOPLIFT_TIMEOUT = 10000;

let initPromise = null;

export const normalizeDiscountCode = (value) => String(value || '').trim();

const comparableDiscountCode = (value) =>
  normalizeDiscountCode(value).toLowerCase();

const getWindow = () => (typeof window !== 'undefined' ? window : null);

export const getAutoApplyDiscountConfig = () => {
  const win = getWindow();

  if (!win) {
    return {};
  }

  win[AUTO_APPLY_CONFIG_KEY] = win[AUTO_APPLY_CONFIG_KEY] || {};

  return win[AUTO_APPLY_CONFIG_KEY];
};

export const waitForShoplift = ({ timeout = SHOPLIFT_TIMEOUT } = {}) => {
  const win = getWindow();

  if (!win) {
    return Promise.resolve(null);
  }

  const startedAt = Date.now();

  return new Promise((resolve) => {
    const check = () => {
      if (typeof win.shoplift?.isHypothesisActive === 'function') {
        resolve(win.shoplift);
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        resolve(null);
        return;
      }

      win.setTimeout(check, 50);
    };

    check();
  });
};

const readDiscountCodeFromElement = (element) =>
  normalizeDiscountCode(
    element?.dataset?.discountCode ||
      element?.dataset?.value ||
      element?.textContent
  );

const getDiscountCodeFromDom = () => {
  const win = getWindow();

  if (!win?.document) {
    return '';
  }

  const selectors = [
    '[data-auto-apply-discount-code]',
    '[data-discount-code-badge][data-value]',
    '.promotion-bar--big [data-copy-to-clipboard][data-value]',
  ];

  for (const selector of selectors) {
    const code = readDiscountCodeFromElement(
      win.document.querySelector(selector)
    );

    if (code) {
      return code;
    }
  }

  return '';
};

export const resolveAutoApplyDiscountCode = () => {
  const config = getAutoApplyDiscountConfig();

  return normalizeDiscountCode(config.discountCode);
};

export const storeAppliedDiscountCode = (code, { auto = false } = {}) => {
  const win = getWindow();
  const discountCode = normalizeDiscountCode(code);

  if (!win || !discountCode) {
    return false;
  }

  try {
    win.sessionStorage.setItem(APPLIED_DISCOUNT_STORAGE_KEY, discountCode);
    if (auto) {
      win.sessionStorage.setItem(
        AUTO_APPLIED_DISCOUNT_STORAGE_KEY,
        discountCode
      );
    }
  } catch (error) {}

  return true;
};

export const clearStoredAutoApplyDiscountCode = () => {
  const win = getWindow();

  if (!win) {
    return false;
  }

  try {
    const autoCode = normalizeDiscountCode(
      win.sessionStorage.getItem(AUTO_APPLIED_DISCOUNT_STORAGE_KEY)
    );
    const appliedCode = normalizeDiscountCode(
      win.sessionStorage.getItem(APPLIED_DISCOUNT_STORAGE_KEY)
    );

    if (
      autoCode &&
      comparableDiscountCode(autoCode) === comparableDiscountCode(appliedCode)
    ) {
      win.sessionStorage.removeItem(APPLIED_DISCOUNT_STORAGE_KEY);
    }

    win.sessionStorage.removeItem(AUTO_APPLIED_DISCOUNT_STORAGE_KEY);
  } catch (error) {}

  return true;
};

export const getStoredAppliedDiscountCode = () => {
  const win = getWindow();

  if (!win) {
    return '';
  }

  try {
    return normalizeDiscountCode(
      win.sessionStorage.getItem(APPLIED_DISCOUNT_STORAGE_KEY)
    );
  } catch (error) {
    return '';
  }
};

const setActiveAutoApplyDiscountCode = (code) => {
  const win = getWindow();
  const config = getAutoApplyDiscountConfig();
  const discountCode = normalizeDiscountCode(code);

  if (!win || !discountCode) {
    return false;
  }

  config.active = true;
  config.activeDiscountCode = discountCode;
  storeAppliedDiscountCode(discountCode, { auto: true });

  try {
    win.document.documentElement.dataset.autoApplyDiscountCodeTest = 'active';
  } catch (error) {}

  try {
    win.dispatchEvent(
      new CustomEvent(AUTO_APPLY_ACTIVE_EVENT, {
        detail: {
          discountCode,
          shopliftId:
            config.shopliftId || AUTO_APPLY_DISCOUNT_SHOPLIFT_ID,
        },
      })
    );
  } catch (error) {}

  return true;
};

export const getActiveAutoApplyDiscountCode = () => {
  const config = getAutoApplyDiscountConfig();

  if (config.active !== true) {
    return '';
  }

  return normalizeDiscountCode(
    config.activeDiscountCode || resolveAutoApplyDiscountCode()
  );
};

export const getCheckoutDiscountCode = () =>
  getStoredAppliedDiscountCode() || getActiveAutoApplyDiscountCode();

export const buildDiscountCheckoutUrl = (
  checkoutUrl = '/checkout',
  discountCode = getCheckoutDiscountCode()
) => {
  const safeCheckoutUrl = checkoutUrl || '/checkout';
  const safeDiscountCode = normalizeDiscountCode(discountCode);

  if (!safeDiscountCode) {

    return safeCheckoutUrl;
  }

  const url = `/discount/${encodeURIComponent(
    safeDiscountCode
  )}?redirect=${encodeURIComponent(safeCheckoutUrl)}`;
  return url;
};

const getDiscountCodeValue = (discount = {}) => {
  if (typeof discount === 'string') {
    return discount;
  }

  return (
    discount.code ||
    discount.title ||
    discount.discount_application?.title ||
    discount.discount_application?.code ||
    ''
  );
};

export const getCartDiscountCodeValues = (cart = {}) => {
  const discounts = [
    ...(Array.isArray(cart?.discount_codes) ? cart.discount_codes : []),
    ...(Array.isArray(cart?.discount_applications)
      ? cart.discount_applications
      : []),
  ];

  return Array.from(
    new Set(
      discounts
        .map(getDiscountCodeValue)
        .map(normalizeDiscountCode)
        .filter(Boolean)
    )
  );
};

export const cartHasDiscountCode = (cart = {}, code) => {
  const expectedCode = comparableDiscountCode(code);

  if (!expectedCode) {
    return false;
  }

  return getCartDiscountCodeValues(cart).some(
    (cartCode) => comparableDiscountCode(cartCode) === expectedCode
  );
};

export const initAutoApplyDiscountCodeTest = () => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const config = getAutoApplyDiscountConfig();
    const shopliftId = normalizeDiscountCode(
      config.shopliftId || AUTO_APPLY_DISCOUNT_SHOPLIFT_ID
    );

    if (!shopliftId && !config.forceEnable) {
      return {
        active: false,
        status: false,
      };
    }

    let active = false;


    if (config.forceEnable) {
      active = true;
      config.resolved = true;

    } else {
      const shoplift = await waitForShoplift();

      if (!shoplift) {
        config.active = false;
        config.resolved = false;
        clearStoredAutoApplyDiscountCode();

        return {
          active: false,
          status: false,
        };
      }

      try {
        active =
          (await Promise.resolve(
            shoplift.isHypothesisActive(shopliftId)
          )) === true;
        config.resolved = true;
      } catch (error) {
        config.active = false;
        config.resolved = false;
        clearStoredAutoApplyDiscountCode();

        return {
          active: false,
          status: false,
        };
      }
    }

    config.active = active;

    if (!active) {
      clearStoredAutoApplyDiscountCode();

      return {
        active: false,
        status: false,
      };
    }

    const discountCode = resolveAutoApplyDiscountCode();

    if (!discountCode) {
      config.error = 'missing_discount_code';

      return {
        active: true,
        status: false,
      };
    }

    setActiveAutoApplyDiscountCode(discountCode);

    return {
      active: true,
      status: true,
      discountCode,
    };
  })();

  return initPromise;
};
